import type { Capability, ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";
import { useState } from "react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";

import type {
  OfferingCatalogEntry,
  PendingRequestView,
  RequestDecision,
  RequestedChange,
  RequesterIdentity,
  ScopeStateView,
} from "../entitlements-api";
import { describeOfferingIncrement } from "./offering-mutation";
import { buildOfferingSections, type OfferingCard } from "./offering-state";
import { OfferingActionDialog } from "./OfferingActionDialog";
import { formatRequestAge, isAged } from "./request-age";
import { decisionWriteBlock, useRequestDecision } from "./request-decision";
import { RequestDenialDialog } from "./RequestDenialDialog";
import { RequesterText } from "./RequesterText";

/**
 * What was asked for, in the requester's own terms.
 *
 * Matched on name AND version, never name alone. Offering versions are
 * immutable and a bump publishes a new one beside the old, so two versions of
 * one name can coexist with different titles; lending one version's title to a
 * request made against another would label the request as something the
 * requester never asked for.
 *
 * With no exact match the bare name is shown. A request naming a version the
 * catalog no longer publishes is exactly the case an operator must be able to
 * see, so it is labelled rather than dropped, or dressed in a title that was
 * never offered at that version.
 */
function changeText(change: RequestedChange, offerings: readonly OfferingCatalogEntry[]): string {
  const title =
    offerings.find(
      (offering) =>
        offering.name === change.offering.name && offering.version === change.offering.version
    )?.title ?? change.offering.name;
  return change.kind === "offering"
    ? `${title} (v${change.offering.version})`
    : `${title}, ${String(change.increment)} more (v${change.offering.version})`;
}

/** The threshold in the units the ages beside it are written in. */
function thresholdText(seconds: number): string {
  if (seconds % 86400 === 0) return `${String(seconds / 86400)}d`;
  if (seconds % 3600 === 0) return `${String(seconds / 3600)}h`;
  /* Seconds below a minute, rather than flooring to "0m". The value is
     configurable per environment, and a threshold misstated as zero is the
     same failure as no threshold at all: the operator cannot tell what the
     alarm is actually watching. */
  if (seconds < 60) return `${String(seconds)}s`;
  return `${String(Math.floor(seconds / 60))}m`;
}

export interface RequestsTabProps {
  readonly requests: readonly PendingRequestView[] | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  /** The platform-wide read filled its page, so this list may be short. */
  readonly capped: boolean;
  /**
   * The age the ENR-1937 queue-age alarm fires at in this environment, or null
   * when the deployment served none. Never defaulted here: see `isAged`.
   */
  readonly maxAgeSeconds: number | null;
  readonly offerings: readonly OfferingCatalogEntry[];
  /** The scope on screen, which every request in this list belongs to. */
  readonly scope: ScopeRef;
  /** Null while a read is in flight, including the refresh a decision triggers. */
  readonly scopeState: ScopeStateView | null;
  readonly capabilities: readonly Capability[];
  /**
   * The platform-staff permissions this operator holds, for deciding whether a
   * control reads as available. The server is still the authority; this only
   * decides what the control looks like before it is pressed.
   */
  readonly permissions: readonly string[];
  /**
   * The current operator's id, so a lost race can say whether the decision
   * that beat them was their own. Optional: absence keeps the wording neutral
   * rather than claiming a peer.
   */
  readonly operatorId?: string;
  /** Fired once a decision lands, so the page re-reads the queue and the scope. */
  readonly onDecided: () => void;
}

/** Which request is being answered, and how, while the confirmation is up. */
interface Confirming {
  readonly request: PendingRequestView;
  readonly decision: RequestDecision;
}

/**
 * This workspace's pending requests, and the decision that answers one.
 *
 * ## Why the outcome banner is rendered above every read state
 *
 * This tab owns a privileged write, so it also owns every state its reads can
 * be in. A decision triggers a re-read, and a branch that replaced the panel
 * while that re-read ran would take the outcome off screen before the operator
 * had read it.
 *
 * The error branch is the worst of them, not a lesser one: the decision
 * COMMITTED, the re-read then failed, so the banner is the only record of it.
 * Swapping the panel for "Failed to read the request queue" would leave the
 * operator with nothing at all about a write that changed a customer's
 * entitlements. That is why the banner sits above the branch rather than
 * inside it, and why the decided row is drawn from the service's own response
 * rather than from the queue: a decided request leaves the pending queue, so
 * the read that follows cannot show what just happened to it.
 *
 * ## One decision at a time
 *
 * One place reports how it went, for the reason the offering cards have one:
 * a per-row status line would let two rows make different claims about a queue
 * they both describe.
 */
export function RequestsTab({
  requests,
  isLoading,
  error,
  capped,
  maxAgeSeconds,
  offerings,
  scope,
  scopeState,
  capabilities,
  permissions,
  operatorId,
  onDecided,
}: RequestsTabProps): React.ReactElement {
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const decision = useRequestDecision({
    onDecided,
    ...(operatorId === undefined ? {} : { operatorId }),
  });
  const busy = decision.pending !== null;

  /* An approval performs the offering apply, so its confirmation is built from
     the same cards the Offerings tab draws, off the same scope-state read.
     Null while that read is in flight or has failed, which is why an approval
     is blocked rather than confirmed with an empty preview.

     A view for any OTHER scope is not an answer about this one. The page's own
     read guard makes that unreachable today; checking it here means a future
     change that breaks it cannot quietly preview one customer's rows in a
     confirmation for another customer's request. */
  const view =
    scopeState !== null && scopeKey(scopeState.scope) === scopeKey(scope) ? scopeState : null;
  const cards: readonly OfferingCard[] | null =
    view === null
      ? null
      : buildOfferingSections({ offerings, scopeState: view, capabilities }).flatMap(
          (section) => section.offerings
        );

  function cardFor(request: PendingRequestView): OfferingCard | null {
    return (
      cards?.find(
        (entry) =>
          entry.offering.name === request.change.offering.name &&
          entry.offering.version === request.change.offering.version
      ) ?? null
    );
  }

  /**
   * Why this request cannot be answered this way, or null.
   *
   * An approval additionally needs a preview to confirm against. Refusing when
   * there is none is the honest branch: the alternative is a confirmation
   * whose value lines are blank for a write that will still write them.
   */
  function blockFor(request: PendingRequestView, kind: RequestDecision): string | null {
    const permissionBlock = decisionWriteBlock(kind, request.scope, permissions);
    if (permissionBlock !== null) return permissionBlock;
    if (kind === "deny") return null;
    if (cards === null) {
      return "This scope has not been read yet, so there is nothing to show you before the approval writes. Re-read the scope and try again.";
    }
    if (cardFor(request) === null) {
      return `The catalog does not publish ${request.change.offering.name} at v${request.change.offering.version}, so what an approval would write cannot be shown. The request was made against a version that is no longer published.`;
    }
    return null;
  }

  const decided = decision.outcome?.kind === "decided" ? decision.outcome.request : null;
  /* Carried from the queue row the operator acted on rather than resolved
     again: the decision routes return no identity, and the operator saw this
     name a moment ago on the row they pressed. */
  const decidedRequester: RequesterIdentity =
    decision.outcome?.kind === "decided" ? decision.outcome.requester : { resolved: false };

  return (
    <div className="space-y-3" data-testid="requests-tab">
      {/* Above every branch below, so an outcome outlives the read a decision
          triggers. Moving these inside a branch would put the result of a
          privileged write behind a placeholder, or behind a read error. */}
      {decision.outcome?.kind === "decided" && (
        <p className="text-sm text-muted-foreground" data-testid="request-outcome-decided">
          {decision.outcome.text}
        </p>
      )}
      {decision.outcome?.kind === "already_handled" && (
        <p className="text-sm text-muted-foreground" data-testid="request-outcome-handled">
          {decision.outcome.text}
        </p>
      )}
      {decision.outcome?.kind === "failed" && (
        <p className="text-sm text-destructive" data-testid="request-outcome-error">
          {decision.outcome.text}
        </p>
      )}

      {decided !== null && (
        <div
          className="rounded-md border px-3 py-2 text-sm"
          data-testid={`request-decided-${decided.id}`}
        >
          <p className="font-medium">{changeText(decided.change, offerings)}</p>
          <p className="text-xs text-muted-foreground">
            <Badge variant="outline">{decided.status}</Badge> requested by{" "}
            <RequesterText
              request={{ requesterId: decided.requesterId, requester: decidedRequester }}
              testId={`requester-decided-${decided.id}`}
            />
          </p>
        </div>
      )}

      {error !== null ? (
        <p className="text-sm text-destructive" data-testid="requests-error">
          Failed to read the request queue: {error}
        </p>
      ) : requests === null ? (
        <p className="text-sm text-muted-foreground" data-testid="requests-loading">
          {isLoading ? "Reading the request queue…" : "No request queue has been read."}
        </p>
      ) : requests.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="requests-empty">
          This workspace has no pending requests.
        </p>
      ) : (
        <ul className="divide-y rounded-md border" data-testid="requests-list">
          {requests.map((request) => {
            const approveBlock = blockFor(request, "approve");
            const denyBlock = blockFor(request, "deny");
            const answering = decision.pending?.requestId === request.id;
            /* The reason is ANNOUNCED with the control rather than only sitting
               near it: a disabled button is skipped by keyboard navigation, so
               proximity alone leaves a screen-reader user with no way to reach
               the explanation. Same reasoning as the offering card. */
            const approveBlockId = `request-approve-block-${request.id}`;
            const denyBlockId = `request-deny-block-${request.id}`;
            return (
              <li
                key={request.id}
                className="space-y-1 px-3 py-2 text-sm"
                data-testid={`request-${request.id}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium">{changeText(request.change, offerings)}</p>
                  {isAged(request, maxAgeSeconds) && (
                    <Badge variant="destructive" data-testid={`request-aged-${request.id}`}>
                      Past the alarm threshold
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  requested by{" "}
                  <RequesterText request={request} testId={`requester-row-${request.id}`} /> ·{" "}
                  <span className="tabular-nums">
                    {formatRequestAge(request.ageSeconds, request.createdAt)}
                  </span>
                </p>
                {request.note !== null && <p className="text-muted-foreground">{request.note}</p>}

                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`request-approve-${request.id}`}
                    disabled={approveBlock !== null || busy}
                    {...(approveBlock === null ? {} : { "aria-describedby": approveBlockId })}
                    onClick={() => setConfirming({ request, decision: "approve" })}
                  >
                    {answering && decision.pending?.decision === "approve"
                      ? "Approving…"
                      : "Approve and apply"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`request-deny-${request.id}`}
                    disabled={denyBlock !== null || busy}
                    {...(denyBlock === null ? {} : { "aria-describedby": denyBlockId })}
                    onClick={() => setConfirming({ request, decision: "deny" })}
                  >
                    {answering && decision.pending?.decision === "deny" ? "Denying…" : "Deny"}
                  </Button>
                </div>
                {/* The reason, not just a greyed-out control: an operator who
                    cannot tell what they are missing goes and asks in a support
                    channel, and one who sees an available control and then 403s
                    goes and asks whether the write half-landed. */}
                {/* One paragraph per action, not one shared. The two reasons
                    differ: at a wide scope an approval names the elevated key
                    a denial never needs, so a single paragraph would announce
                    the approval's reason to anyone reaching Deny. */}
                {approveBlock !== null && (
                  <p
                    id={approveBlockId}
                    className="text-xs text-muted-foreground"
                    data-testid={approveBlockId}
                  >
                    {approveBlock}
                  </p>
                )}
                {denyBlock !== null && (
                  <p
                    id={denyBlockId}
                    className="text-xs text-muted-foreground"
                    data-testid={denyBlockId}
                  >
                    {denyBlock}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {maxAgeSeconds === null ? (
        <p className="text-xs text-muted-foreground" data-testid="requests-threshold-unknown">
          This deployment did not report a queue-age alarm threshold, so no request here is flagged
          as overdue. That is not the same as none being overdue.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="requests-threshold">
          A request older than <span className="tabular-nums">{thresholdText(maxAgeSeconds)}</span>{" "}
          is flagged, which is when the queue-age alarm fires in this environment.
        </p>
      )}

      {capped && (
        <p className="text-sm text-muted-foreground" data-testid="requests-capped">
          The queue read returned a full page, so this workspace may have older pending requests
          that are not listed here.
        </p>
      )}

      {confirming?.decision === "approve" &&
        cardFor(confirming.request) !== null &&
        /* Non-null by the guard above; the block that gates the control refuses
           an approval with no card, so this branch cannot render without one. */
        (() => {
          const card = cardFor(confirming.request);
          if (card === null) return null;
          const change = confirming.request.change;
          /* A quota increase is a DIFFERENT write from an apply: the service
             grows each quota lever on top of what the scope holds. Previewing
             it with the apply's "set to" lines would confirm a privileged
             write against numbers it never produces. */
          const preview =
            change.kind === "quota_increase"
              ? describeOfferingIncrement(card, confirming.request.scope, change.increment)
              : undefined;
          return (
            <OfferingActionDialog
              action="apply"
              card={card}
              scope={confirming.request.scope}
              busy={busy}
              {...(preview === undefined ? {} : { preview })}
              heading={
                change.kind === "quota_increase"
                  ? `Approve ${String(change.increment)} more of ${card.offering.title}?`
                  : `Approve and apply ${card.offering.title}?`
              }
              confirmLabel={
                change.kind === "quota_increase" ? "Approve increase" : "Approve and apply"
              }
              inFlightLabel="Approving…"
              context={
                <p data-testid="request-approval-context">
                  Asked for by{" "}
                  <RequesterText
                    request={confirming.request}
                    testId={`requester-approval-${confirming.request.id}`}
                  />{" "}
                  <span className="tabular-nums">
                    {formatRequestAge(confirming.request.ageSeconds, confirming.request.createdAt)}
                  </span>
                  .{confirming.request.note !== null && ` They wrote: ${confirming.request.note}`}
                </p>
              }
              onCancel={() => setConfirming(null)}
              onConfirm={() => {
                void decision.run("approve", confirming.request).finally(() => {
                  setConfirming(null);
                });
              }}
            />
          );
        })()}

      {confirming?.decision === "deny" && (
        <RequestDenialDialog
          request={confirming.request}
          label={changeText(confirming.request.change, offerings)}
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={(reason) => {
            void decision.run("deny", confirming.request, reason).finally(() => {
              setConfirming(null);
            });
          }}
        />
      )}
    </div>
  );
}
